import {
  DashboardState,
  ESdChangeReason,
  EWFEventType,
  EWFNodeType,
  IStartProcessTrigger,
  IWFEvent,
  IWFParameter,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import BooleanSvg from "../../../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeSvg from "../../../../../svgs/parameter-type-icons/date-time.svg";
import DecimalSvg from "../../../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../../../svgs/parameter-type-icons/double.svg";
import GuidSvg from "../../../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../../../svgs/parameter-type-icons/int64.svg";
import StringSvg from "../../../../../svgs/parameter-type-icons/string.svg";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { requestProcessEditorTab } from "../../../flows/process-editor/process-editor-tab-bus";
import { PROCESS_TAB_PARAMETERS } from "../../../flows/process-editor/process-editor-tabs";
import ActionSectionEmptyState from "../../properties-panel/action-section-empty-state";
import "./workflow-event-properties.scss";

export interface IWorkFlowEventPropertiesState {}

export interface IWorkFlowEventPropertiesProps {
  selectedNodeId: string | undefined;
}

interface IResolvedTrigger {
  trigger: IStartProcessTrigger;
  sourceTypeLabel: string;
  sourceName: string;
}

class WorkFlowEventProperties extends PureSdComponent<
  IWorkFlowEventPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkFlowEventPropertiesState
> {
  // Source ids we've already issued a load for. Keyed per source type so a job
  // and an api with the same id can't collide. Used to dedupe load attempts:
  // loadScheduledJobs/loadApiManagements always end with updateProject
  // (OtherSetStates), which re-renders this panel and re-enters componentDidUpdate.
  // Without this set, a trigger pointing at a deleted (orphan) entity would never
  // resolve, keeping the "unresolved" condition true and looping forever.
  private attemptedJobSourceIds = new Set<string>();
  private attemptedApiSourceIds = new Set<string>();

  constructor(props: IWorkFlowEventPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
  }

  componentDidMount(): void {
    this.ensureTriggerSourcesLoaded();
  }

  componentDidUpdate(): void {
    this.ensureTriggerSourcesLoaded();
  }

  /** Returns the workflow id when a Start event node is selected, otherwise undefined. */
  private getSelectedStartWorkflowId = (): string | undefined => {
    const wf = this.props.sd.selectedWf;
    if (!wf?.id) return undefined;
    const event = this.props.sd.findNodeRecursively(this.props.selectedNodeId!, EWFNodeType.Event, wf) as
      | IWFEvent
      | undefined;
    if (event?.eventType !== EWFEventType.Start) return undefined;
    return wf.id;
  };

  /**
   * Trigger source names live in the lazily-loaded scheduledJobs/apiManagements
   * lists, not in the project json (which only holds the id-based trigger index).
   * When a Start node is selected, load a list whenever this workflow has a
   * trigger whose source isn't resolvable from what's currently in memory — this
   * covers jobs/apis created after the first load, not just the initial fetch.
   * Each unresolved source id is loaded at most once (attempted-set dedupe) so a
   * trigger pointing at a deleted entity can't loop forever.
   */
  private ensureTriggerSourcesLoaded = (): void => {
    const workflowId = this.getSelectedStartWorkflowId();
    if (!workflowId) return;
    const sd = this.props.sd;
    const projectId = sd.project?.id;
    if (!projectId) return;
    const triggers = sd.project?.startProcessTriggers?.[workflowId] ?? [];

    const jobIds = new Set((sd.scheduledJobs ?? []).map((j) => j.id));
    const unresolvedJob = triggers.some(
      (t) => t.sourceType === "ScheduledJob" && !jobIds.has(t.sourceId) && !this.attemptedJobSourceIds.has(t.sourceId)
    );
    if (unresolvedJob) {
      triggers.forEach((t) => {
        if (t.sourceType === "ScheduledJob") this.attemptedJobSourceIds.add(t.sourceId);
      });
      sd.loadScheduledJobs(projectId);
    }

    const apiIds = new Set((sd.apiManagements ?? []).map((a) => a.id));
    const unresolvedApi = triggers.some(
      (t) => t.sourceType === "ApiManagement" && !apiIds.has(t.sourceId) && !this.attemptedApiSourceIds.has(t.sourceId)
    );
    if (unresolvedApi) {
      triggers.forEach((t) => {
        if (t.sourceType === "ApiManagement") this.attemptedApiSourceIds.add(t.sourceId);
      });
      sd.loadApiManagements(projectId);
    }
  };

  private resolveTriggers = (workflowId: string | undefined): IResolvedTrigger[] => {
    if (!workflowId) return [];
    const triggers = this.props.sd.project?.startProcessTriggers?.[workflowId] ?? [];
    const screens = this.props.sd.screenList ?? [];
    const jobs = this.props.sd.scheduledJobs ?? [];
    const apis = this.props.sd.apiManagements ?? [];
    const resolved: IResolvedTrigger[] = [];
    for (const t of triggers) {
      if (t.sourceType === "Screen") {
        const s = screens.find((x) => x.id === t.sourceId);
        if (!s) continue;
        resolved.push({ trigger: t, sourceTypeLabel: "Screen", sourceName: s.name });
      } else if (t.sourceType === "ScheduledJob") {
        const j = jobs.find((x) => x.id === t.sourceId);
        if (!j) continue;
        resolved.push({ trigger: t, sourceTypeLabel: "Scheduled Job", sourceName: j.name });
      } else if (t.sourceType === "ApiManagement") {
        const a = apis.find((x) => x.id === t.sourceId);
        if (!a) continue;
        resolved.push({ trigger: t, sourceTypeLabel: "API", sourceName: a.name });
      }
    }
    return resolved;
  };

  private navigateToTriggerSource = (trigger: IStartProcessTrigger): void => {
    if (trigger.sourceType === "Screen") {
      this.props.sd.clearSelectedWf();
      this.props.sd.setDashboardState(DashboardState.design);
      this.props.sd.loadScreen(trigger.sourceId);
    } else if (trigger.sourceType === "ScheduledJob") {
      this.props.sd.clearSelectedWf();
      this.props.sd.setDashboardState(DashboardState.scheduledJobs);
      this.props.sd.setActiveScheduledJob(trigger.sourceId);
    } else if (trigger.sourceType === "ApiManagement") {
      this.props.sd.clearSelectedWf();
      this.props.sd.setDashboardState(DashboardState.apiManagement);
      this.props.sd.setActiveApi(trigger.sourceId);
    }
  };

  getTypeIcon = (type) => {
    switch (type) {
      case "Unknown":
        return <StringSvg />;
      case "Boolean":
        return <BooleanSvg />;
      case "Bytea":
        return <DoubleSvg />;
      case "Byte":
        return <DoubleSvg />;
      case "DateTimeOffset":
        return <DateTimeSvg />;
      case "Decimal":
        return <DecimalSvg />;
      case "Double":
        return <DoubleSvg />;
      case "Guid":
        return <GuidSvg />;
      case "Int16":
        return <Int16Svg />;
      case "Int32":
        return <Int32Svg />;
      case "Int64":
        return <Int64Svg />;
      case "Single":
        return <DoubleSvg />;
      case "String":
        return <StringSvg />;
      case "Image":
        return <ImageSvg />;
      default:
        return <StringSvg />;
    }
  };

  /** One glyph per trigger source type, matching the design's row icons. */
  private triggerIcon = (sourceType: IStartProcessTrigger["sourceType"]): string => {
    if (sourceType === "Screen") return "device-desktop";
    if (sourceType === "ScheduledJob") return "clock-hour-10";
    return "api";
  };

  /** The empty-card link jumps to the Parameters tab, where inputs/outputs are managed. */
  private goToParameters = (): void => {
    requestProcessEditorTab(PROCESS_TAB_PARAMETERS);
  };

  render() {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    if (!wf || wf.id.length === 0) return <></>;

    const event = this.props.sd.findNodeRecursively(this.props.selectedNodeId!, EWFNodeType.Event, wf) as
      | IWFEvent
      | undefined;
    const isStart: boolean = event?.eventType === EWFEventType.Start;

    const parameters: IWFParameter[] = wf.parameters ?? [];
    const shownParameters: IWFParameter[] = isStart
      ? parameters.filter((p: IWFParameter): boolean => p.isInput === true)
      : parameters.filter((p: IWFParameter): boolean => p.isOutput === true);
    const resolvedTriggers: IResolvedTrigger[] = isStart ? this.resolveTriggers(wf.id) : [];

    const paramsLabel: string = isStart ? "Inputs" : "Outputs";
    const paramsSub: string = isStart
      ? "Input parameters this process receives when it starts"
      : "Output parameters this process receives when it ends";

    return (
      <div className="kuika-wf-event">
        <section className="kuika-wf-event__section">
          <div className="kuika-wf-event__section-head">
            <span className="kuika-wf-event__section-title">Process {paramsLabel}</span>
            <span className="kuika-wf-event__section-sub">{paramsSub}</span>
          </div>
          {shownParameters.length > 0 ? (
            <div className="kuika-wf-event__param-list">
              {shownParameters.map((p: IWFParameter) => (
                <div key={p.id} className="kuika-wf-event__param">
                  <span className="kuika-wf-event__param-icon">{this.getTypeIcon(p.dataType?.toString())}</span>
                  <span className="kuika-wf-event__param-name">{p.name}</span>
                  <span className="kuika-wf-event__param-type">({p.dataType?.toString()})</span>
                </div>
              ))}
            </div>
          ) : (
            <ActionSectionEmptyState
              message={`No ${paramsLabel} defined yet.`}
              actionLabel="Go to Parameters"
              onAdd={this.goToParameters}
              flush
            />
          )}
        </section>

        {isStart && (
          <section className="kuika-wf-event__section">
            <div className="kuika-wf-event__section-head">
              <span className="kuika-wf-event__section-title">Process Triggers</span>
              <span className="kuika-wf-event__section-sub">
                Screens, APIs and Scheduled Jobs currently configured to start this process.
              </span>
            </div>
            {resolvedTriggers.length > 0 ? (
              <div className="kuika-wf-event__trigger-list">
                {resolvedTriggers.map((r: IResolvedTrigger) => (
                  <button
                    type="button"
                    key={`${r.trigger.sourceType}-${r.trigger.sourceId}-${r.trigger.actionId}`}
                    className={classNames("kuika-wf-event__trigger", OVERFLOW_REVEAL_HOST_CLASS)}
                    onClick={(): void => this.navigateToTriggerSource(r.trigger)}
                  >
                    <KuikaSvg
                      name={this.triggerIcon(r.trigger.sourceType)}
                      width={14}
                      height={14}
                      color="var(--dark-6)"
                    />
                    <span className="kuika-wf-event__trigger-name">
                      <KuikaOverflowText>{r.sourceName}</KuikaOverflowText>
                    </span>
                    <span className="kuika-wf-event__trigger-type">({r.sourceTypeLabel})</span>
                  </button>
                ))}
              </div>
            ) : (
              <ActionSectionEmptyState
                message="No triggers yet."
                hint="Start it from a screen, API or job action."
                flush
              />
            )}
          </section>
        )}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.SaveWorkflow || changeReason === ESdChangeReason.OtherSetStates) {
      return true;
    }
    return false;
  };
}

const workFlowEventProperties = withContext<IWorkFlowEventPropertiesProps>(
  withSD<IWorkFlowEventPropertiesProps>(WorkFlowEventProperties)
);
export { workFlowEventProperties as WorkFlowEventProperties };
